import { supabase } from './supabaseClient'

export const authService = {
  // Login function that checks against the users table
  async login(email, password) {
    try {
      // Query the users table for the email
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .eq('status', 'active') // Only allow active users to login
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Invalid email or password')
        }
        throw new Error('Login failed: ' + error.message)
      }

      if (!users) {
        throw new Error('Invalid email or password')
      }

      // For demo purposes, compare passwords directly
      // In production, you should hash passwords on the server side
      const isPasswordValid = password === users.password
      
      if (!isPasswordValid) {
        throw new Error('Invalid email or password')
      }

      // Update last login timestamp
      await supabase
        .from('users')
        .update({ lastlogin: new Date().toISOString() })
        .eq('userid', users.userid)

      // Return user data (excluding password)
      const { password: _, ...userWithoutPassword } = users
      return {
        success: true,
        user: userWithoutPassword
      }

    } catch (error) {
      return {
        success: false,
        error: error.message
      }
    }
  },

  // Store user session in localStorage
  setUserSession(user) {
    localStorage.setItem('user', JSON.stringify(user))
    // Dispatch custom event to notify auth state change
    window.dispatchEvent(new Event('authChange'))
  },

  // Get user session from localStorage
  getUserSession() {
    const user = localStorage.getItem('user')
    return user ? JSON.parse(user) : null
  },

  // Clear user session
  logout() {
    localStorage.removeItem('user')
    // Clear all event suppression flags from session storage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('hideEvent_')) {
        sessionStorage.removeItem(key);
      }
    });
    // Dispatch custom event to notify auth state change
    window.dispatchEvent(new Event('authChange'))
  },

  // Check if user is authenticated
  isAuthenticated() {
    return this.getUserSession() !== null
  },

  // Update user profile
  async updateProfile(userId, profileData) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          firstname: profileData.firstname,
          lastname: profileData.lastname,
          email: profileData.email,
          phone: profileData.phone,
          address: profileData.address
        })
        .eq('userid', userId)
        .select()
        .single()

      if (error) throw error

      // Update local session
      const { password: _, ...userWithoutPassword } = data
      this.setUserSession(userWithoutPassword)

      return { success: true, data: userWithoutPassword }
    } catch (error) {
      console.error('Error updating profile:', error)
      return { success: false, error: error.message }
    }
  },

  // Change password
  async changePassword(userId, currentPassword, newPassword) {
    try {
      // First verify current password
      const { data: user, error: fetchError } = await supabase
        .from('users')
        .select('password')
        .eq('userid', userId)
        .single()

      if (fetchError) throw fetchError

      // Check if current password matches
      if (user.password !== currentPassword) {
        return { success: false, error: 'Current password is incorrect' }
      }

      // Update password
      const { error: updateError } = await supabase
        .from('users')
        .update({ password: newPassword })
        .eq('userid', userId)

      if (updateError) throw updateError

      return { success: true }
    } catch (error) {
      console.error('Error changing password:', error)
      return { success: false, error: error.message }
    }
  }
}
